import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { api } from "./api";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useNavigate } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";
import { toast } from "@shared/ui/use-toast";
import { logEvent } from "@shared/analytics/analytics";
import { useCallback, useState } from "react";
import { useToast } from "@shared/ui/use-toast";

export const useGetAllMeterCycle = ({
  page,
  search,
  meterType,
  status,
  startDate,
  endDate,
}) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.list(
      page,
      search,
      meterType,
      status,
      startDate,
      endDate
    ),
    () =>
      api.meterCycle.landing.list(
        page,
        search,
        meterType,
        status,
        startDate,
        endDate,
        utilityId
      ),
    {
      enabled: !!utilityId,
      staleTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );
};

export const useMeterCycleExport = (
  utilityId: string,
  search?: string,
  meterType?: string,
  status?: string,
  startDate?: string,
  endDate?: string
) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const blob = await api.meterCycle.landing.exportAllMeterCycles(
        undefined, // No page for export to get all data
        utilityId,
        search,
        meterType,
        status,
        startDate,
        endDate
      );

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export read cycles: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [
    utilityId,
    search,
    meterType,
    status,
    startDate,
    endDate,
    isExporting,
    toast,
  ]);

  return { exportCSV, isExporting };
};

export const useGetReadCycleSummary = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.summary(),
    () => api.meterCycle.landing.summary(utilityId),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetReadCycleDetails = (cycleId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.details(cycleId),
    () => api.meterCycle.view.details(cycleId),
    {
      enabled: !!cycleId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetReadCycleDetailsforEdit = (cycleId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.details(cycleId),
    () => api.meterCycle.update.editReadCycleDetails(cycleId),
    {
      enabled: !!cycleId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetRouteDetails = (
  page?,
  limit?,
  cycleId?: any,
  utilityService?: any,
  readType?: any,
  searchQuery?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.routeDetails(
      page,
      cycleId,
      utilityService,
      readType,
      searchQuery
    ),
    () =>
      api.meterCycle.view.routeDetails(
        utilityId,
        page,
        limit,
        cycleId,
        utilityService,
        readType,
        searchQuery
      ),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};
export const useGetAssignedRoutes = (
  page?,
  limit?,
  cycleId?: any,
  utilityService?: any,
  searchQuery?: any,
  isAssigned?: number
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.assignedRoutes(
      page,
      cycleId,
      utilityService,
      searchQuery,
      isAssigned
    ),
    () =>
      api.meterCycle.view.getAssignedRoutes(
        utilityId,
        page,
        limit,
        cycleId,
        utilityService,
        searchQuery,
        isAssigned
      ),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetMetersDetails = (
  cycleId: any,
  page: any,
  searchQuery?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.metersDetails(
      cycleId,
      page,
      searchQuery
    ),
    () =>
      api.meterCycle.view.metersDetails(utilityId, cycleId, page, searchQuery),
    {
      enabled: !!cycleId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useReadCycleAuditTrail = (cycleId: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.auditTrail(cycleId),
    () => api.meterCycle.view.auditTrail(cycleId, utilityId),
    {
      enabled: !!cycleId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetMeterSummaryDashboard = (cycleId: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterSummaryDashboard(
      cycleId
    ),
    () => api.meterCycle.dashboard.getMeterSummaryDashboard(cycleId, utilityId),
    {
      enabled: !!cycleId && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useCreateReadCycle = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) => api.meterCycle.create.createReadCycle(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Read cycle created successfully.",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meterCycle"],
        });

        navigate("/mx/read-cycle/");
      },
      onError: (err: Error) => {
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
        logEvent("Read Cycle Creation Failed", {
          error: err.message,
        });
      },
    }
  );
};

export const useUpdateReadCycle = (cycleId: any) => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) => api.meterCycle.create.updateReadCycle(payload, cycleId),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Read Cycle updated successfully!",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meterCycle"],
        });

        navigate("/mx/read-cycle/");
      },
    }
  );
};

export const useGetArea = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.area(),
    () => api.meterCycle.onboarding.getArea(utilityId),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useGetSubArea = (codeList: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.subArea(codeList),
    () => api.meterCycle.onboarding.getSubArea(codeList, utilityId),
    {
      enabled: !!codeList && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useUtilityServices = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.global.utility.list(),
    () => api.meterCycle.utility.getServices(utilityId),
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetMeterType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterType(),
    () => api.meterCycle.meterType.getMeterType(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetMeterSummaryDashboardByRouteId = (routeIds?: any[]) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterSummaryDashboardByRouteId(
      routeIds
    ),
    () =>
      api.meterCycle.dashboard.getMeterSummaryDashboardByRouteId(
        utilityId,
        routeIds
      ),
    {
      enabled: !!routeIds && !!utilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const usePauseReadCycle = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartMutation(
    (meterCycleId: string) =>
      api.meterCycle.update.pauseReadCycle(utilityId, meterCycleId),
    {
      onSuccess: (updatedServiceCategory, variables) => {
        toast({
          title: "Success",
          description: "Read cycle paused successfully",
          variant: "default",
        });
      },
      onError: (error: any, variables) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to pause read cycle",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetFrequency = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.frequency(),
    () => api.meterCycle.create.getFrequency(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetWeekDays = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.weekDays(),
    () => api.meterCycle.create.getWeekDays(utilityId),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetMonths = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.months(),
    () => api.meterCycle.create.getMonths(utilityId),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useCreateScheduleReadCycle = () => {
  return useSmartMutation(
    (payload: any) => api.meterCycle.create.createScheduleReadCycle(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Schedule created successfully",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle"],
        });
      },
    }
  );
};

export const useUpdateScheduleReadCycle = (scheduleId: any) => {
  return useSmartMutation(
    (payload: any) =>
      api.schedule.create.updateScheduleReadCycle(scheduleId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Schedule updated successfully",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meterCycle"],
        });
      },
    }
  );
};

export const useValidationRules = (open: boolean) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.validationRules(),
    () => api.meterCycle.view.getValidationRules(utilityId),
    { enabled: !!utilityId && !!open }
  );
};

export const useGetMeterRunHistory = (
  cycleId?: any,
  page?: any,
  searchQuery?: any,
  status?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterRunHistory(
      cycleId,
      page,
      searchQuery,
      status
    ),
    () =>
      api.meterCycle.view.getMeterRunHistory(
        cycleId,
        utilityId,
        page,
        searchQuery,
        status
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetPerformanceDashboard = (
  meterCycleId: any,
  runId?: number
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getPerformanceDashboard(
      meterCycleId,
      runId
    ),
    () =>
      api.meterCycle.view.getPerformanceDashboard(
        meterCycleId,
        utilityId,
        runId
      ),
    {
      enabled: !!meterCycleId && !!utilityId,
    }
  );
};

export const useGetAllMeterCycleForMoveRoutes = (
  open: boolean,
  routeIds?: any[],
  page?: number,
  search?: string,
  meterType?: string,
  status?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getAllMeterCycleForMoveRoutes(
      routeIds,
      page,
      search,
      meterType,
      status,
      utilityId
    ),
    () =>
      api.meterCycle.view.getAllMeterCycleForMoveRoutes(
        page,
        search,
        meterType,
        status,
        utilityId
      ),
    {
      enabled: !!open,
    }
  );
};

export const useUpdateRoute = (moveCycleId: number, cycleId: number) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.meterCycle.view.updateRoute(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Route Updated",
          description: "Route updated successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "meterCycle"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useGetRules = (
  ruleType: number,
  open: boolean,
  meterCycleId?: number
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getRules(
      ruleType,
      utilityId,
      meterCycleId
    ),
    () => api.meterCycle.view.getRules(ruleType, utilityId, meterCycleId),
    {
      enabled: !!open || !!utilityId || !!ruleType,
    }
  );
};

export const useCreateRules = (meterCycleId?: string) => {
  const { toast } = useToast();
  const utilityId = useRemoteUtilityId();
  return useSmartMutation(
    (payload: any) => api.meterCycle.view.createRules(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Handle Rule",
          description: _data?.message,
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "getRules", 0, utilityId],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "getRules", 1, utilityId],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "details", meterCycleId],
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useAddRoutes = (meterCycleId?: string) => {
  const { toast } = useToast();
  const utilityId = useRemoteUtilityId();
  return useSmartMutation(
    (payload: any) => api.meterCycle.view.addRoutes(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Add Routes",
          description: "Routes added successfully.",
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useGetPremise = (
  code_name: any[],
  page?: number,
  limit?: number,
  area?: string,
  sub_area?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getPremise(
      code_name,
      page,
      limit,
      area,
      sub_area
    ),
    () =>
      api.meterCycle.view.getPremise(
        remoteUtilityId,
        code_name,
        page,
        limit,
        area,
        sub_area
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetPremiseBySearch = (
  code_name: any[],
  searchQuery?: string,
  page?: number,
  limit?: number,
) => {
  const remoteUtilityId = useRemoteUtilityId();
  const search_field = "territory_json-premise";
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getPremiseBySearch(
      code_name,
      search_field,
      searchQuery,
      page,
      limit,
    ),
    () =>
      api.meterCycle.view.getPremiseBySearch(
        remoteUtilityId,
        code_name,
        search_field,
        searchQuery,
        page,
        limit,
      ),
    {
      enabled: !!remoteUtilityId && searchQuery?.length >= 1,
    }
  );
};

export const useAreaList = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.mx.readCycleManagement.areaList()],
    () => api.meterCycle.view.getTerritoryConfigMap("area", remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useSubAreaList = (areaList: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.mx.readCycleManagement.subAreaList(areaList)],
    () =>
      api.meterCycle.view.getTerritoryConfigMap(
        "sub_area",
        remoteUtilityId,
        areaList
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetScheduleReview = (
  frequency?: string,
  startDate?: string,
  time?: string,
  endDate?: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getScheduleReview(
      frequency,
      startDate,
      time,
      endDate
    ),
    () =>
      api.meterCycle.create.getScheduleReview(
        frequency,
        startDate,
        time,
        endDate
      ),
    {
      enabled: !!frequency || !!startDate || !!time || !!endDate,
    }
  );
};

export const useGetMeterScheduleList = (
  page?: number,
  limit?: number,
  frequency?: string,
  status?: string,
  readType?: string,
  startDate?: string,
  endDate?: string,
  search?: string
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getMeterScheduleList(
      page,
      limit,
      frequency,
      status,
      readType,
      startDate,
      endDate,
      search
    ),
    () =>
      api.schedule.list.getMeterScheduleList(
        remoteUtilityId,
        page,
        limit,
        frequency,
        status,
        readType,
        startDate,
        endDate,
        search
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetMeterScheduleSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.mx.readCycleManagement.getMeterScheduleSummary(),
    ],
    () => api.schedule.dashboard.getMeterScheduleSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetMeterScheduleDetails = (scheduleId: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getMeterScheduleDetails(
      scheduleId
    ),
    () =>
      api.schedule.details.getMeterScheduleDetails(remoteUtilityId, scheduleId),
    {
      enabled: !!remoteUtilityId && !!scheduleId,
    }
  );
};

export const usePauseMeterSchedule = (scheduleId: string) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.schedule.details.pauseMeterSchedule(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Pause Meter Schedule",
          description: _data?.message,
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "getMeterScheduleDetails", scheduleId],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "getMeterScheduleList"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useRunMeterSchedule = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.schedule.details.runMeterSchedule(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Run Meter Schedule",
          description: _data?.message,
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "getMeterScheduleList"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "meterCycle", "getMeterScheduleDetails"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useGetMeterRunHistoryForSchedule = (
  scheduleId?: any,
  page?: any,
  searchQuery?: any,
  status?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterRunHistoryForSchedule(
      scheduleId,
      page,
      searchQuery,
      status
    ),
    () =>
      api.schedule.details.getMeterRunHistoryForSchedule(
        scheduleId,
        utilityId,
        page,
        searchQuery,
        status
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllReadCycle = (status: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getAllReadCycle(status),
    () => api.schedule.create.getAllReadCycle(utilityId, status),
    {
      enabled: !!utilityId,
    }
  );
};
